import { useEffect, useRef, useState, useCallback } from "react";
import { api, wsUrl } from "./api";
import * as log from "./logger";

/**
 * Realtime WebSocket hook. Opens a WS for the given orgId, reconnects with
 * exponential backoff, and forwards messages to `onEvent`.
 */
export function useWebSocket(orgId, onEvent) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  useEffect(() => {
    if (!orgId) return undefined;
    let closed = false;
    let cancel = false;
    let backoff = 1000;

    const boot = async () => {
      try {
        const { data } = await api.get("/auth/ws-ticket");
        if (cancel) return;
        const url = wsUrl(data.token, orgId);
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => { setConnected(true); backoff = 1000; };
        ws.onclose = () => {
          setConnected(false);
          if (!closed) {
            setTimeout(boot, backoff);
            backoff = Math.min(backoff * 2, 15000);
          }
        };
        ws.onerror = (err) => {
          log.debug("[ws] error", err);
        };
        ws.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (cbRef.current) cbRef.current(payload);
          } catch (err) {
            log.debug("[ws] bad message", err);
          }
        };
      } catch (err) {
        log.debug("[ws] ticket failed, retrying", err);
        if (!closed) setTimeout(boot, backoff);
      }
    };
    boot();
    return () => {
      closed = true;
      cancel = true;
      if (wsRef.current) wsRef.current.close();
    };
  }, [orgId]);

  return { connected, send };
}
